import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import * as XLSX from "xlsx";
import { TableHead, Th, Tr, Td } from "../../Elements/DataTable";
import {
  HiOutlineChartSquareBar,
  HiOutlineCurrencyEuro,
  HiOutlineExclamationCircle,
  HiOutlinePencilAlt,
  HiOutlineTrendingDown,
  HiOutlineTrendingUp,
} from "react-icons/hi";

import { UserContext } from "../../../context/UserContext";
import apiFetch from "../../../context/api";
import { VENUE_TYPES } from "../../../lib/consts/venueTypes";
import ButtonBar from "../../Elements/ButtonBar";
import ProductModal from "../ProductsComponents/ProductModal";

function formatCurrency(value) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatNumber(value, digits = 2) {
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value || 0));
}

function formatPercent(value, digits = 1) {
  return `${formatNumber(value, digits)}%`;
}

function SummaryCard({ label, value, hint, icon, accentClass }) {
  return (
    <div className="flex min-w-0 flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm max-lg:portrait:overflow-visible max-xl:landscape:h-full max-xl:landscape:overflow-hidden max-xl:landscape:p-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <div className={`shrink-0 rounded-lg p-2 text-white max-xl:landscape:p-1.5 ${accentClass}`}>{icon}</div>
        <div className="min-w-0 text-xs font-semibold uppercase leading-snug tracking-wide text-gray-500 break-words max-lg:portrait:normal-case max-lg:portrait:tracking-normal">
          {label}
        </div>
      </div>
      <div className="mt-3 text-2xl font-bold tracking-tight text-gray-900 tabular-nums break-words max-lg:portrait:text-xl max-xl:landscape:mt-2 max-xl:landscape:text-xl">
        {value}
      </div>
      {hint ? (
        <div className="mt-1.5 text-xs leading-relaxed text-gray-500 break-words max-lg:portrait:line-clamp-none max-xl:landscape:mt-auto max-xl:landscape:pt-1.5 max-xl:landscape:line-clamp-2">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function SignalList({ title, subtitle, rows, accentClass, emptyText, onEditProduct, icon }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          <div className="text-xs text-gray-500">{subtitle}</div>
        </div>
        <div className={`rounded-xl p-2.5 text-white shrink-0 ${accentClass}`}>{icon}</div>
      </div>

      <div className="mt-4 space-y-2">
        {rows.length === 0 ? (
          <div className="rounded-lg bg-gray-50 px-3 py-4 text-sm text-gray-500">{emptyText}</div>
        ) : (
          rows.map((row) => (
            <div
              key={row.product_id}
              className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-3 max-lg:portrait:gap-3 lg:flex-row lg:items-center lg:justify-between"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900 max-lg:portrait:whitespace-normal max-lg:portrait:break-words lg:truncate">
                  {row.product_name}
                </div>
                <div className="text-xs text-gray-500 max-lg:portrait:whitespace-normal max-lg:portrait:break-words lg:truncate">
                  {row.category_name || "Sin categoria"} · {row.supplier_name || "Sin proveedor"}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 max-lg:portrait:w-full max-lg:portrait:justify-between lg:shrink-0">
                {"contribution_total" in row && row.contribution_total != null ? (
                  <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-gray-700">
                    {formatCurrency(row.contribution_total)}
                  </span>
                ) : null}
                {"gross_margin_percent" in row && row.gross_margin_percent != null ? (
                  <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-gray-700">
                    {formatNumber(row.gross_margin_percent, 2)}%
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() => onEditProduct(row.product_id)}
                  className="inline-flex shrink-0 items-center justify-center gap-1 rounded-lg bg-lime-600 px-3 py-2 text-xs font-semibold text-white hover:bg-lime-700 max-lg:portrait:min-h-[36px] max-lg:portrait:min-w-[88px] lg:px-2.5 lg:py-1.5"
                >
                  <HiOutlinePencilAlt size={14} />
                  Editar
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function EngineeringQuadrant({ title, subtitle, rows, accentClass, emptyText, onEditProduct }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          <div className="text-xs text-gray-500">{subtitle}</div>
        </div>
        <div className={`rounded-full px-2.5 py-1 text-xs font-bold text-white ${accentClass}`}>
          {rows.length}
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {rows.length === 0 ? (
          <div className="rounded-lg bg-gray-50 px-3 py-4 text-sm text-gray-500">{emptyText}</div>
        ) : (
          rows.map((row) => (
            <div key={row.product_id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-gray-900">{row.product_name}</div>
                  <div className="truncate text-xs text-gray-500">
                    {row.category_name || "Sin categoria"} · {row.supplier_name || "Sin proveedor"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onEditProduct(row.product_id)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-lime-600 px-2 py-1.5 text-xs font-semibold text-white hover:bg-lime-700"
                >
                  <HiOutlinePencilAlt size={14} />
                  Editar
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-gray-700">
                  Unidades: {formatNumber(row.units_sold, 3)}
                </span>
                {/* «Unidades» es lo COBRADO: es el divisor del precio y del margen, y el
                    eje de popularidad del análisis de menú (BE #600). Lo que salió del
                    armario se enseña al lado, y solo cuando difiere — en los productos por
                    unidades es siempre el mismo número y repetirlo es ruido. */}
                {row.dispensed != null && Number(row.dispensed) !== Number(row.units_sold) ? (
                  <span
                    className="rounded-full bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800"
                    title="Gramos que salieron del armario (báscula). La diferencia con las unidades cobradas es la merma."
                  >
                    Despachado: {formatNumber(row.dispensed, 3)}
                  </span>
                ) : null}
                <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-gray-700">
                  Contribucion: {formatCurrency(row.contribution_total)}
                </span>
                <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-gray-700">
                  Margen: {row.gross_margin_percent == null ? "-" : `${formatNumber(row.gross_margin_percent, 2)}%`}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function NetMarginPanel({ title, subtitle, rows, accentClass, emptyText, onEditProduct, formatMetric }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          <div className="text-xs text-gray-500">{subtitle}</div>
        </div>
        <div className={`rounded-xl px-2.5 py-1 text-xs font-bold text-white ${accentClass}`}>{rows.length}</div>
      </div>

      <div className="mt-4 space-y-2">
        {rows.length === 0 ? (
          <div className="rounded-lg bg-gray-50 px-3 py-4 text-sm text-gray-500">{emptyText}</div>
        ) : (
          rows.map((row) => (
            <div key={row.product_id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-gray-900">{row.product_name}</div>
                  <div className="truncate text-xs text-gray-500">
                    {row.category_name || "Sin categoria"} · {row.supplier_name || "Sin proveedor"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onEditProduct(row.product_id)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-lime-600 px-2 py-1.5 text-xs font-semibold text-white hover:bg-lime-700"
                >
                  <HiOutlinePencilAlt size={14} />
                  Editar
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-gray-700">
                  Neto / ud: {row.net_margin_amount == null ? "-" : formatCurrency(row.net_margin_amount)}
                </span>
                <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-gray-700">
                  Neto %: {row.net_margin_percent == null ? "-" : `${formatNumber(row.net_margin_percent, 2)}%`}
                </span>
                <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-gray-700">
                  {formatMetric(row)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function AlertsPanel({ alerts, onEditProduct, settingsComplete }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-700">Alertas</div>
          <h2 className="mt-1 text-xl font-bold text-gray-900">Acciones prioritarias</h2>
          <p className="mt-1 text-sm text-amber-900/80">
            Productos con riesgo operativo o datos incompletos según el filtro actual.
          </p>
        </div>
        <div className="rounded-xl bg-white px-3 py-2 text-sm text-gray-700">
          Estado configuración operativa:{" "}
          <span className={`font-semibold ${settingsComplete ? "text-emerald-700" : "text-amber-700"}`}>
            {settingsComplete ? "Completa" : "Incompleta"}
          </span>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {alerts.length === 0 ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
            No hay alertas críticas con el filtro actual.
          </div>
        ) : (
          alerts.map((alert) => (
            <div key={alert.id} className={`rounded-xl border px-4 py-4 ${alert.containerClass}`}>
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className={`text-xs font-semibold uppercase tracking-[0.2em] ${alert.eyebrowClass}`}>{alert.level}</div>
                  <div className="mt-1 text-base font-semibold text-gray-900">{alert.title}</div>
                  <div className="mt-1 text-sm text-gray-700">{alert.description}</div>
                </div>
                {alert.productId ? (
                  <button
                    type="button"
                    onClick={() => onEditProduct(alert.productId)}
                    className="inline-flex items-center gap-1 rounded-lg bg-lime-600 px-3 py-2 text-sm font-semibold text-white hover:bg-lime-700"
                  >
                    <HiOutlinePencilAlt size={15} />
                    Revisar producto
                  </button>
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function ProfitabilityPage() {
  const [token, , ownedShops, venueID, venueName, , , accessPrivileges, , , , venueType] = useContext(UserContext);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [settingsMessage, setSettingsMessage] = useState("");

  const [search, setSearch] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showMissingCostOnly, setShowMissingCostOnly] = useState(false);
  const [showLowMarginOnly, setShowLowMarginOnly] = useState(false);
  const [activeProductId, setActiveProductId] = useState(null);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [operationalSettings, setOperationalSettings] = useState({
    default_sale_vat_rate: "",
    labour_cost_per_hour: "",
    utilities_monthly_cost: "",
    rent_monthly_cost: "",
    social_security_monthly_cost: "",
    packaging_monthly_cost: "",
    allocation_mode: "per_unit",
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const canEditOperationalSettings = accessPrivileges === "owner" || accessPrivileges === "superadmin";

  const normalizeSettingsNumber = useCallback((value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(String(value).replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (venueID) params.set("venue_id", venueID);
    if (search.trim()) params.set("search", search.trim());
    if (supplierFilter.trim()) params.set("supplier", supplierFilter.trim());
    if (fromDate) params.set("date_from", `${fromDate}T00:00:00`);
    if (toDate) params.set("date_to", `${toDate}T23:59:59`);
    return params.toString();
  }, [venueID, search, supplierFilter, fromDate, toDate]);

  const loadData = useCallback(async () => {
    if (!token || !venueID) {
      setLoaded(true);
      return;
    }

    setLoaded(false);
    setError("");

    try {
      const params = buildParams();
      const [summaryRes, productsRes] = await Promise.all([
        apiFetch(`/v1/profitability/summary?${params}`, { headers: authHeaders }),
        apiFetch(`/v1/profitability/products?${params}`, { headers: authHeaders }),
      ]);

      if (!summaryRes.ok) throw new Error(`Error ${summaryRes.status} cargando resumen`);
      if (!productsRes.ok) throw new Error(`Error ${productsRes.status} cargando rentabilidad`);

      const [summaryData, productsData] = await Promise.all([summaryRes.json(), productsRes.json()]);
      setSummary(summaryData);
      setRows(Array.isArray(productsData) ? productsData : []);
    } catch (e) {
      setError(e.message || "Error cargando rentabilidad");
      setSummary(null);
      setRows([]);
    } finally {
      setLoaded(true);
    }
  }, [authHeaders, buildParams, token, venueID]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadOperationalSettings = useCallback(async () => {
    if (!token || !venueID) {
      setSettingsLoaded(true);
      return;
    }

    setSettingsLoaded(false);
    try {
      const response = await apiFetch(`/v1/profitability/operational-cost-settings?venue_id=${venueID}`, {
        headers: authHeaders,
      });
      if (!response.ok) {
        throw new Error(`Error ${response.status} cargando costes operativos`);
      }
      const data = await response.json();
      setOperationalSettings({
        default_sale_vat_rate: data.default_sale_vat_rate ?? "",
        labour_cost_per_hour: data.labour_cost_per_hour ?? "",
        utilities_monthly_cost: data.utilities_monthly_cost ?? "",
        rent_monthly_cost: data.rent_monthly_cost ?? "",
        social_security_monthly_cost: data.social_security_monthly_cost ?? "",
        packaging_monthly_cost: data.packaging_monthly_cost ?? "",
        allocation_mode: data.allocation_mode || "per_unit",
      });
    } catch (e) {
      setError(e.message || "Error cargando costes operativos");
    } finally {
      setSettingsLoaded(true);
    }
  }, [authHeaders, token, venueID]);

  useEffect(() => {
    loadOperationalSettings();
  }, [loadOperationalSettings]);

  const visibleRows = useMemo(() => {
    const base = Array.isArray(rows) ? rows : [];
    return base.filter((row) => {
      if (showMissingCostOnly && !row.missing_cost) return false;
      if (showLowMarginOnly && !(row.gross_margin_percent != null && Number(row.gross_margin_percent) < 30)) return false;
      if (categoryFilter && (row.category_name || "") !== categoryFilter) return false;
      return true;
    });
  }, [rows, showMissingCostOnly, showLowMarginOnly, categoryFilter]);

  const supplierOptions = useMemo(() => {
    return Array.from(
      new Set((rows || []).map((row) => row.supplier_name).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, "es"));
  }, [rows]);

  const categoryOptions = useMemo(() => {
    return Array.from(
      new Set((rows || []).map((row) => row.category_name).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, "es"));
  }, [rows]);

  const topMarginRows = useMemo(() => {
    return [...visibleRows]
      .filter((row) => !row.missing_cost && row.gross_margin_percent != null)
      .sort((a, b) => Number(b.gross_margin_percent || 0) - Number(a.gross_margin_percent || 0))
      .slice(0, 5);
  }, [visibleRows]);

  const lowMarginRows = useMemo(() => {
    return [...visibleRows]
      .filter((row) => !row.missing_cost && row.gross_margin_percent != null)
      .sort((a, b) => Number(a.gross_margin_percent || 0) - Number(b.gross_margin_percent || 0))
      .slice(0, 5);
  }, [visibleRows]);

  const missingCostRows = useMemo(() => {
    return [...visibleRows]
      .filter((row) => row.missing_cost)
      .sort((a, b) => Number(b.revenue_ex_vat || 0) - Number(a.revenue_ex_vat || 0))
      .slice(0, 5);
  }, [visibleRows]);

  const topNetMarginRows = useMemo(() => {
    return [...visibleRows]
      .filter((row) => row.net_contribution_total != null)
      .sort((a, b) => Number(b.net_contribution_total || 0) - Number(a.net_contribution_total || 0))
      .slice(0, 5);
  }, [visibleRows]);

  const lowNetMarginRows = useMemo(() => {
    return [...visibleRows]
      .filter((row) => row.net_margin_percent != null && Number(row.net_margin_percent) >= 0 && Number(row.net_margin_percent) < 10)
      .sort((a, b) => Number(a.net_margin_percent || 0) - Number(b.net_margin_percent || 0))
      .slice(0, 5);
  }, [visibleRows]);

  const zeroOperationalCostRows = useMemo(() => {
    return [...visibleRows]
      .filter((row) => !row.missing_cost && row.operational_cost_per_unit != null && Number(row.operational_cost_per_unit) <= 0)
      .sort((a, b) => Number(b.revenue_ex_vat || 0) - Number(a.revenue_ex_vat || 0))
      .slice(0, 5);
  }, [visibleRows]);

  const negativeNetMarginRows = useMemo(() => {
    return [...visibleRows]
      .filter((row) => row.net_margin_amount != null && Number(row.net_margin_amount) < 0)
      .sort((a, b) => Number(a.net_margin_amount || 0) - Number(b.net_margin_amount || 0))
      .slice(0, 5);
  }, [visibleRows]);

  const menuEngineering = useMemo(() => {
    const eligibleRows = visibleRows.filter(
      (row) => !row.missing_cost && row.contribution_total != null && Number(row.units_sold || 0) > 0
    );

    const averageUnits = eligibleRows.length
      ? eligibleRows.reduce((acc, row) => acc + Number(row.units_sold || 0), 0) / eligibleRows.length
      : 0;
    const averageContribution = eligibleRows.length
      ? eligibleRows.reduce((acc, row) => acc + Number(row.contribution_total || 0), 0) / eligibleRows.length
      : 0;

    const stars = [];
    const puzzles = [];
    const plowhorses = [];
    const dogs = [];

    eligibleRows.forEach((row) => {
      const units = Number(row.units_sold || 0);
      const contribution = Number(row.contribution_total || 0);
      const isHighPopularity = units >= averageUnits;
      const isHighContribution = contribution >= averageContribution;

      if (isHighPopularity && isHighContribution) {
        stars.push(row);
      } else if (!isHighPopularity && isHighContribution) {
        puzzles.push(row);
      } else if (isHighPopularity && !isHighContribution) {
        plowhorses.push(row);
      } else {
        dogs.push(row);
      }
    });

    const byContributionDesc = (a, b) => Number(b.contribution_total || 0) - Number(a.contribution_total || 0);
    const byContributionAsc = (a, b) => Number(a.contribution_total || 0) - Number(b.contribution_total || 0);

    return {
      averageUnits,
      averageContribution,
      eligibleCount: eligibleRows.length,
      stars: stars.sort(byContributionDesc),
      puzzles: puzzles.sort(byContributionDesc),
      plowhorses: plowhorses.sort(byContributionAsc),
      dogs: dogs.sort(byContributionAsc),
    };
  }, [visibleRows]);

  const operationalSummary = useMemo(() => {
    const labour = normalizeSettingsNumber(operationalSettings.labour_cost_per_hour) || 0;
    const utilities = normalizeSettingsNumber(operationalSettings.utilities_monthly_cost) || 0;
    const rent = normalizeSettingsNumber(operationalSettings.rent_monthly_cost) || 0;
    const socialSecurity = normalizeSettingsNumber(operationalSettings.social_security_monthly_cost) || 0;
    const packaging = normalizeSettingsNumber(operationalSettings.packaging_monthly_cost) || 0;
    const totalMonthlyFixed = utilities + rent + socialSecurity + packaging;
    const completedFields = [
      operationalSettings.labour_cost_per_hour,
      operationalSettings.utilities_monthly_cost,
      operationalSettings.rent_monthly_cost,
      operationalSettings.social_security_monthly_cost,
      operationalSettings.packaging_monthly_cost,
    ].filter((value) => value !== "" && value !== null && value !== undefined).length;

    return {
      labour,
      totalMonthlyFixed,
      completedFields,
    };
  }, [normalizeSettingsNumber, operationalSettings]);

  const omnesMetrics = useMemo(() => {
    const eligibleRows = visibleRows.filter(
      (row) =>
        !row.missing_cost &&
        Number(row.units_sold || 0) > 0 &&
        row.gross_margin_amount != null
    );

    const totalUnits = eligibleRows.reduce((acc, row) => acc + Number(row.units_sold || 0), 0);
    const totalRevenue = eligibleRows.reduce((acc, row) => acc + Number(row.revenue_ex_vat || 0), 0);
    const totalGrossContribution = eligibleRows.reduce((acc, row) => acc + Number(row.contribution_total || 0), 0);
    const totalNetContribution = eligibleRows.reduce((acc, row) => acc + Number(row.net_contribution_total || 0), 0);
    const pricePoints = eligibleRows
      .map((row) => {
        const explicitUnitPrice = Number(row.unit_sale_price_ex_vat || 0);
        if (explicitUnitPrice > 0) return explicitUnitPrice;
        const units = Number(row.units_sold || 0);
        return units > 0 ? Number(row.revenue_ex_vat || 0) / units : 0;
      })
      .filter((price) => price > 0)
      .sort((a, b) => a - b);
    const weightedGrossMarginPercent = totalRevenue > 0 ? (totalGrossContribution / totalRevenue) * 100 : 0;
    const weightedNetMarginPercent = totalRevenue > 0 ? (totalNetContribution / totalRevenue) * 100 : 0;
    const averageRevenuePerUnit = totalUnits > 0 ? totalRevenue / totalUnits : 0;
    const averageGrossPerUnit = totalUnits > 0 ? totalGrossContribution / totalUnits : 0;
    const averageNetPerUnit = totalUnits > 0 ? totalNetContribution / totalUnits : 0;
    const minPrice = pricePoints.length ? pricePoints[0] : 0;
    const maxPrice = pricePoints.length ? pricePoints[pricePoints.length - 1] : 0;
    const averagePrice = pricePoints.length
      ? pricePoints.reduce((acc, price) => acc + price, 0) / pricePoints.length
      : 0;
    const priceRange = maxPrice - minPrice;
    const priceSpreadRatio = minPrice > 0 ? maxPrice / minPrice : 0;
    const topRevenueRows = [...eligibleRows]
      .sort((a, b) => Number(b.revenue_ex_vat || 0) - Number(a.revenue_ex_vat || 0))
      .slice(0, 5);
    const topFiveRevenue = topRevenueRows.reduce((acc, row) => acc + Number(row.revenue_ex_vat || 0), 0);
    const topFiveRevenueShare = totalRevenue > 0 ? (topFiveRevenue / totalRevenue) * 100 : 0;
    const weightedFoodCostPercent = totalRevenue > 0 ? ((totalRevenue - totalGrossContribution) / totalRevenue) * 100 : 0;
    const labourCostShare = totalRevenue > 0 && summary ? (Number(summary.operational_cost_total || 0) / totalRevenue) * 100 : 0;
    const structureCostShare = totalRevenue > 0 && operationalSummary.totalMonthlyFixed > 0
      ? (operationalSummary.totalMonthlyFixed / totalRevenue) * 100
      : 0;
    const lowerBandThreshold = averagePrice > 0 ? averagePrice * 0.85 : 0;
    const upperBandThreshold = averagePrice > 0 ? averagePrice * 1.15 : 0;
    const priceBands = [
      {
        key: "low",
        label: averagePrice > 0 ? `< ${formatCurrency(lowerBandThreshold)}` : "Banda baja",
        count: 0,
      },
      {
        key: "mid",
        label: averagePrice > 0 ? `${formatCurrency(lowerBandThreshold)} - ${formatCurrency(upperBandThreshold)}` : "Banda media",
        count: 0,
      },
      {
        key: "high",
        label: averagePrice > 0 ? `> ${formatCurrency(upperBandThreshold)}` : "Banda alta",
        count: 0,
      },
    ];

    pricePoints.forEach((price) => {
      if (price < lowerBandThreshold) {
        priceBands[0].count += 1;
      } else if (price > upperBandThreshold) {
        priceBands[2].count += 1;
      } else {
        priceBands[1].count += 1;
      }
    });

    const distributionByPriceBand = priceBands.map((band) => ({
      ...band,
      share: pricePoints.length ? (band.count / pricePoints.length) * 100 : 0,
    }));

    const recommendations = [];
    if (pricePoints.length === 0) {
      recommendations.push("No hay suficientes datos de precio para calcular OMNES con el filtro actual.");
    } else {
      if (distributionByPriceBand[0].share >= 55) {
        recommendations.push("La carta está muy concentrada en ticket bajo. Falta escalera de valor hacia tickets medios y altos.");
      }
      if (distributionByPriceBand[2].share >= 35) {
        recommendations.push("Hay bastante peso en ticket alto. Revisa que el volumen de salida acompañe para no frenar rotación.");
      }
      if (priceSpreadRatio > 3.2) {
        recommendations.push("La dispersión de precios es muy amplia. Conviene revisar coherencia de carta y percepción de cliente.");
      }
      if (topFiveRevenueShare > 60) {
        recommendations.push("Los 5 productos principales concentran demasiado ingreso. Interesa diversificar ventas.");
      }
      if (weightedFoodCostPercent > 35) {
        recommendations.push("El food cost medio está alto para restauración. Revisa escandallos, mermas o precios.");
      }
      if (weightedNetMarginPercent < 8) {
        recommendations.push("El margen neto medio es bajo. La carta vende, pero puede no estar dejando caja suficiente.");
      }
    }

    const classifyHealth = () => {
      if (eligibleRows.length === 0) return { label: "Sin datos", toneClass: "bg-gray-100 text-gray-700" };
      if (weightedNetMarginPercent >= 18 && menuEngineering.stars.length >= menuEngineering.dogs.length) {
        return { label: "Carta sana", toneClass: "bg-emerald-100 text-emerald-800" };
      }
      if (weightedNetMarginPercent >= 8) {
        return { label: "Carta estable", toneClass: "bg-amber-100 text-amber-800" };
      }
      return { label: "Carta en riesgo", toneClass: "bg-rose-100 text-rose-800" };
    };

    return {
      eligibleCount: eligibleRows.length,
      totalUnits,
      totalRevenue,
      averagePrice,
      minPrice,
      maxPrice,
      priceRange,
      priceSpreadRatio,
      weightedGrossMarginPercent,
      weightedFoodCostPercent,
      weightedNetMarginPercent,
      labourCostShare,
      structureCostShare,
      averageRevenuePerUnit,
      averageGrossPerUnit,
      averageNetPerUnit,
      topFiveRevenueShare,
      distributionByPriceBand,
      recommendations,
      starsShare: eligibleRows.length ? (menuEngineering.stars.length / eligibleRows.length) * 100 : 0,
      plowhorsesShare: eligibleRows.length ? (menuEngineering.plowhorses.length / eligibleRows.length) * 100 : 0,
      dogsShare: eligibleRows.length ? (menuEngineering.dogs.length / eligibleRows.length) * 100 : 0,
      health: classifyHealth(),
    };
  }, [menuEngineering, operationalSummary.totalMonthlyFixed, summary, visibleRows]);

  const openProductEditor = useCallback((productId) => {
    setActiveProductId(productId);
    setIsProductModalOpen(true);
  }, []);

  const closeProductEditor = useCallback(() => {
    setIsProductModalOpen(false);
    setActiveProductId(null);
  }, []);

  const handleOperationalSettingsChange = useCallback((event) => {
    const { name, value } = event.target;
    setOperationalSettings((prev) => ({ ...prev, [name]: value }));
  }, []);

  const saveOperationalSettings = useCallback(async () => {
    if (!token || !venueID || !canEditOperationalSettings) return;
    setIsSavingSettings(true);
    setSettingsMessage("");
    try {
      const payload = {
        default_sale_vat_rate: normalizeSettingsNumber(operationalSettings.default_sale_vat_rate),
        labour_cost_per_hour: normalizeSettingsNumber(operationalSettings.labour_cost_per_hour),
        utilities_monthly_cost: normalizeSettingsNumber(operationalSettings.utilities_monthly_cost),
        rent_monthly_cost: normalizeSettingsNumber(operationalSettings.rent_monthly_cost),
        social_security_monthly_cost: normalizeSettingsNumber(operationalSettings.social_security_monthly_cost),
        packaging_monthly_cost: normalizeSettingsNumber(operationalSettings.packaging_monthly_cost),
        allocation_mode: operationalSettings.allocation_mode || "per_unit",
      };
      const response = await apiFetch(`/v1/profitability/operational-cost-settings?venue_id=${venueID}`, {
        method: "PUT",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Error ${response.status} guardando costes operativos`);
      }
      const data = await response.json();
      setOperationalSettings({
        default_sale_vat_rate: data.default_sale_vat_rate ?? "",
        labour_cost_per_hour: data.labour_cost_per_hour ?? "",
        utilities_monthly_cost: data.utilities_monthly_cost ?? "",
        rent_monthly_cost: data.rent_monthly_cost ?? "",
        social_security_monthly_cost: data.social_security_monthly_cost ?? "",
        packaging_monthly_cost: data.packaging_monthly_cost ?? "",
        allocation_mode: data.allocation_mode || "per_unit",
      });
      setSettingsMessage("Costes operativos guardados.");
    } catch (e) {
      setSettingsMessage(e.message || "No se pudieron guardar los costes operativos.");
    } finally {
      setIsSavingSettings(false);
    }
  }, [authHeaders, canEditOperationalSettings, normalizeSettingsNumber, operationalSettings, token, venueID]);

  const exportExcel = useCallback(() => {
    if (!visibleRows.length) {
      setError("No hay filas filtradas para exportar.");
      return;
    }

    const exportRows = visibleRows.map((row) => ({
      Producto: row.product_name,
      Categoria: row.category_name || "-",
      Proveedor: row.supplier_name || "-",
      "Unidades vendidas": Number(row.units_sold || 0),
      "Gramos despachados": row.dispensed == null ? "" : Number(row.dispensed),
      "Venta sin IVA": Number(row.revenue_ex_vat || 0),
      "Precio unitario sin IVA": Number(row.unit_sale_price_ex_vat || 0),
      "Coste unitario sin IVA": row.unit_cost_ex_vat == null ? "" : Number(row.unit_cost_ex_vat),
      "Margen bruto €/ud": row.gross_margin_amount == null ? "" : Number(row.gross_margin_amount),
      "Margen bruto %": row.gross_margin_percent == null ? "" : Number(row.gross_margin_percent),
      "Contribucion total": row.contribution_total == null ? "" : Number(row.contribution_total),
      "Coste operativo €/ud": row.operational_cost_per_unit == null ? "" : Number(row.operational_cost_per_unit),
      "Margen neto €/ud": row.net_margin_amount == null ? "" : Number(row.net_margin_amount),
      "Margen neto %": row.net_margin_percent == null ? "" : Number(row.net_margin_percent),
      "Contribucion neta": row.net_contribution_total == null ? "" : Number(row.net_contribution_total),
      "Sin coste": row.missing_cost ? "Si" : "No",
      "Bajo margen": row.gross_margin_percent != null && Number(row.gross_margin_percent) < 30 ? "Si" : "No",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rentabilidad");

    if (venueType === VENUE_TYPES.BISTRO) {
      const omnesSheetRows = [
        { Indicador: "Precio medio s/IVA", Valor: Number(omnesMetrics.averagePrice || 0) },
        { Indicador: "Precio mínimo s/IVA", Valor: Number(omnesMetrics.minPrice || 0) },
        { Indicador: "Precio máximo s/IVA", Valor: Number(omnesMetrics.maxPrice || 0) },
        { Indicador: "Rango de precios", Valor: Number(omnesMetrics.priceRange || 0) },
        { Indicador: "Ratio dispersión", Valor: Number(omnesMetrics.priceSpreadRatio || 0) },
        { Indicador: "Food cost %", Valor: Number(omnesMetrics.weightedFoodCostPercent || 0) },
        { Indicador: "Margen bruto %", Valor: Number(omnesMetrics.weightedGrossMarginPercent || 0) },
        { Indicador: "Margen neto %", Valor: Number(omnesMetrics.weightedNetMarginPercent || 0) },
        { Indicador: "Peso top 5 facturación %", Valor: Number(omnesMetrics.topFiveRevenueShare || 0) },
        { Indicador: "Mix estrellas %", Valor: Number(omnesMetrics.starsShare || 0) },
        { Indicador: "Mix caballos de batalla %", Valor: Number(omnesMetrics.plowhorsesShare || 0) },
        { Indicador: "Mix perros %", Valor: Number(omnesMetrics.dogsShare || 0) },
      ];
      const omnesDistributionRows = omnesMetrics.distributionByPriceBand.map((band) => ({
        Banda: band.label,
        Productos: band.count,
        "Peso %": Number(band.share || 0),
      }));
      const recommendationRows = omnesMetrics.recommendations.map((item, index) => ({
        Orden: index + 1,
        Recomendacion: item,
      }));

      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(omnesSheetRows), "OMNES");
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(omnesDistributionRows), "OMNES Bandas");
      if (recommendationRows.length) {
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(recommendationRows), "OMNES Recomend.");
      }
    }

    XLSX.writeFile(workbook, `rentabilidad-productos-${new Date().toISOString().slice(0, 10)}.xlsx`);
  }, [omnesMetrics, venueType, visibleRows]);

  const menuBar = {
    title: "Rentabilidad",
    buttons: [
      { label: "Exportar Excel", color: "green", onClick: exportExcel },
      { label: "Recargar", color: "blue", onClick: loadData },
    ],
  };

  const summaryCards = useMemo(() => {
    if (!summary) return [];
    return [
      {
        label: "Ventas sin IVA",
        value: formatCurrency(summary.revenue_ex_vat),
        hint: `${summary.products_sold_count || 0} productos vendidos`,
        icon: <HiOutlineCurrencyEuro size={22} />,
        accentClass: "bg-emerald-600",
      },
      {
        label: "Coste estimado",
        value: formatCurrency(summary.estimated_cost_ex_vat),
        hint: "Segun coste actual del producto",
        icon: <HiOutlineChartSquareBar size={22} />,
        accentClass: "bg-cyan-600",
      },
      {
        label: "Margen bruto",
        value: formatCurrency(summary.gross_margin_total),
        hint: `${formatNumber(summary.gross_margin_percent || 0)}% margen`,
        icon: <HiOutlineTrendingUp size={22} />,
        accentClass: "bg-lime-600",
      },
      {
        label: "Productos sin coste",
        value: String(summary.products_missing_cost_count || 0),
        hint: "Requieren coste para analisis completo",
        icon: <HiOutlineExclamationCircle size={22} />,
        accentClass: "bg-amber-500",
      },
      {
        label: "Coste operativo",
        value: formatCurrency(summary.operational_cost_total),
        hint: "Labor, energia, packaging y reparto fijo",
        icon: <HiOutlineChartSquareBar size={22} />,
        accentClass: "bg-sky-700",
      },
      {
        label: "Margen neto",
        value: formatCurrency(summary.net_margin_total),
        hint: `${formatNumber(summary.net_margin_percent || 0)}% neto`,
        icon: <HiOutlineTrendingDown size={22} />,
        accentClass: "bg-indigo-700",
      },
    ];
  }, [summary]);

  const alerts = useMemo(() => {
    const items = [];

    if (operationalSummary.completedFields < 5) {
      items.push({
        id: "operational-settings-incomplete",
        level: "Alta prioridad",
        title: "Faltan costes operativos del local por completar",
        description: "Completa alquiler, suministros, seguridad social, packaging común y coste/hora para que el margen neto sea fiable.",
        containerClass: "border-amber-300 bg-white",
        eyebrowClass: "text-amber-700",
      });
    }

    negativeNetMarginRows.forEach((row) => {
      items.push({
        id: `negative-net-${row.product_id}`,
        level: "Crítico",
        title: `${row.product_name} tiene margen neto negativo`,
        description: `Pierde ${formatCurrency(row.net_margin_amount)} por unidad y aporta ${formatCurrency(row.net_contribution_total)} netos en el periodo.`,
        productId: row.product_id,
        containerClass: "border-rose-300 bg-white",
        eyebrowClass: "text-rose-700",
      });
    });

    lowNetMarginRows.forEach((row) => {
      items.push({
        id: `low-net-${row.product_id}`,
        level: "Media",
        title: `${row.product_name} tiene margen neto bajo`,
        description: `Está en ${formatNumber(row.net_margin_percent, 2)}% neto. Revisa precio, coste o tiempo de elaboración.`,
        productId: row.product_id,
        containerClass: "border-orange-300 bg-white",
        eyebrowClass: "text-orange-700",
      });
    });

    missingCostRows.forEach((row) => {
      items.push({
        id: `missing-cost-${row.product_id}`,
        level: "Media",
        title: `${row.product_name} no tiene coste de compra`,
        description: "No puede calcularse bien la rentabilidad sin coste unitario base.",
        productId: row.product_id,
        containerClass: "border-amber-300 bg-white",
        eyebrowClass: "text-amber-700",
      });
    });

    zeroOperationalCostRows.forEach((row) => {
      items.push({
        id: `zero-op-${row.product_id}`,
        level: "Baja",
        title: `${row.product_name} no tiene coste operativo aplicado`,
        description: "Revisa tiempo de elaboración, energía o packaging por unidad para mejorar el margen neto.",
        productId: row.product_id,
        containerClass: "border-sky-300 bg-white",
        eyebrowClass: "text-sky-700",
      });
    });

    return items.slice(0, 10);
  }, [lowNetMarginRows, missingCostRows, negativeNetMarginRows, operationalSummary.completedFields, zeroOperationalCostRows]);

  return (
    <div className="flex flex-col gap-4 p-0 sm:p-2 md:p-4">
      <div className="hidden md:block">
        <ButtonBar title={menuBar.title} buttons={menuBar.buttons} />
      </div>

      <div className="block md:hidden bg-white p-4 rounded-lg shadow border border-gray-200 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-xl font-semibold text-gray-800">Rentabilidad</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportExcel}
              className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded"
            >
              Excel
            </button>
            <button
              type="button"
              onClick={loadData}
              className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded"
            >
              Recargar
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top_left,_rgba(132,204,22,0.22),_transparent_40%),linear-gradient(135deg,_#0f172a,_#14532d_55%,_#ecfccb)] p-6 text-white shadow-lg">
        <div className="text-xs font-semibold uppercase tracking-[0.28em] text-lime-200">Rentabilidad y margen</div>
        <h1 className="mt-2 text-2xl font-bold md:text-3xl">Control de margen por producto</h1>
        <p className="mt-1 text-sm text-lime-50 opacity-80">
          Coste, proveedor, margen bruto y contribucion total.
          {venueName ? ` · ${venueName}` : ""}
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 max-xl:landscape:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-6 [&>*]:min-w-0 items-stretch">
        {(loaded ? summaryCards : [
          { label: "Ventas sin IVA", value: "…", hint: "", icon: <HiOutlineCurrencyEuro size={22} />, accentClass: "bg-emerald-600" },
          { label: "Coste estimado", value: "…", hint: "", icon: <HiOutlineChartSquareBar size={22} />, accentClass: "bg-cyan-600" },
          { label: "Margen bruto", value: "…", hint: "", icon: <HiOutlineTrendingUp size={22} />, accentClass: "bg-lime-600" },
          { label: "Productos sin coste", value: "…", hint: "", icon: <HiOutlineExclamationCircle size={22} />, accentClass: "bg-amber-500" },
          { label: "Coste operativo", value: "…", hint: "", icon: <HiOutlineChartSquareBar size={22} />, accentClass: "bg-sky-700" },
          { label: "Margen neto", value: "…", hint: "", icon: <HiOutlineTrendingDown size={22} />, accentClass: "bg-indigo-700" },
        ]).map((card) => (
          <SummaryCard key={card.label} {...card} />
        ))}
      </div>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 space-y-3">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Producto, categoria, proveedor..."
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor</label>
            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-500"
            >
              <option value="">Todos</option>
              {supplierOptions.map((supplier) => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-500"
            >
              <option value="">Todas</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-500"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 pt-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showMissingCostOnly}
              onChange={(e) => setShowMissingCostOnly(e.target.checked)}
              className="rounded border-gray-300 text-lime-600 focus:ring-lime-500"
            />
            Mostrar solo productos sin coste
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showLowMarginOnly}
              onChange={(e) => setShowLowMarginOnly(e.target.checked)}
              className="rounded border-gray-300 text-lime-600 focus:ring-lime-500"
            />
            Mostrar solo margen bajo (&lt; 30%)
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={loadData}
              className="px-4 py-2 bg-lime-500 hover:bg-lime-600 text-white text-sm font-semibold rounded transition-colors"
            >
              Filtrar
            </button>
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setSupplierFilter("");
                setCategoryFilter("");
                setFromDate("");
                setToDate("");
                setShowMissingCostOnly(false);
                setShowLowMarginOnly(false);
              }}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded transition-colors"
            >
              Limpiar
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <SignalList
          title="Top margen"
          subtitle="Productos con mejor margen bruto porcentual"
          rows={topMarginRows}
          accentClass="bg-emerald-600"
          emptyText="No hay suficientes datos para calcular top margen."
          onEditProduct={openProductEditor}
          icon={<HiOutlineTrendingUp size={20} />}
        />
        <SignalList
          title="Bajo margen"
          subtitle="Productos a revisar antes de vender más"
          rows={lowMarginRows}
          accentClass="bg-rose-600"
          emptyText="No hay productos con margen calculado."
          onEditProduct={openProductEditor}
          icon={<HiOutlineTrendingDown size={20} />}
        />
        <SignalList
          title="Sin coste"
          subtitle="Productos vendidos sin coste informado"
          rows={missingCostRows}
          accentClass="bg-amber-500"
          emptyText="No hay productos sin coste dentro del filtro actual."
          onEditProduct={openProductEditor}
          icon={<HiOutlineExclamationCircle size={20} />}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <NetMarginPanel
          title="Top margen neto"
          subtitle="Productos que mas aportan despues de coste operativo"
          rows={topNetMarginRows}
          accentClass="bg-indigo-700"
          emptyText="No hay datos netos suficientes con el filtro actual."
          onEditProduct={openProductEditor}
          formatMetric={(row) => `Aporte neto: ${formatCurrency(row.net_contribution_total)}`}
        />
        <NetMarginPanel
          title="Margen neto negativo"
          subtitle="Productos que pierden dinero despues del reparto operativo"
          rows={negativeNetMarginRows}
          accentClass="bg-rose-700"
          emptyText="No hay productos con margen neto negativo en el filtro actual."
          onEditProduct={openProductEditor}
          formatMetric={(row) => `Impacto neto: ${formatCurrency(row.net_contribution_total)}`}
        />
      </div>

      <AlertsPanel
        alerts={alerts}
        onEditProduct={openProductEditor}
        settingsComplete={operationalSummary.completedFields >= 5}
      />

      {venueType === VENUE_TYPES.BISTRO ? (
        <div className="rounded-2xl border border-orange-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.24em] text-orange-600">OMNES</div>
              <h2 className="mt-1 text-xl font-bold text-gray-900">Lectura rápida de carta para restaurante</h2>
              <p className="mt-1 text-sm text-gray-500">
                Capa inicial inspirada en OMNES y menu engineering, calculada sobre el filtro actual.
              </p>
            </div>
            <div className={`inline-flex rounded-full px-3 py-2 text-sm font-semibold ${omnesMetrics.health.toneClass}`}>
              {omnesMetrics.health.label}
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="Ticket medio por item"
              value={formatCurrency(omnesMetrics.averageRevenuePerUnit)}
              hint={`${formatNumber(omnesMetrics.totalUnits, 0)} unidades analizadas`}
              icon={<HiOutlineCurrencyEuro size={22} />}
              accentClass="bg-orange-500"
            />
            <SummaryCard
              label="Margen bruto medio"
              value={`${formatNumber(omnesMetrics.weightedGrossMarginPercent, 2)}%`}
              hint={`${formatCurrency(omnesMetrics.averageGrossPerUnit)} por unidad`}
              icon={<HiOutlineTrendingUp size={22} />}
              accentClass="bg-emerald-600"
            />
            <SummaryCard
              label="Margen neto medio"
              value={`${formatNumber(omnesMetrics.weightedNetMarginPercent, 2)}%`}
              hint={`${formatCurrency(omnesMetrics.averageNetPerUnit)} por unidad`}
              icon={<HiOutlineTrendingDown size={22} />}
              accentClass="bg-indigo-700"
            />
            <SummaryCard
              label="Concentración top 5"
              value={formatPercent(omnesMetrics.topFiveRevenueShare, 2)}
              hint="Peso de los 5 items que más facturan"
              icon={<HiOutlineChartSquareBar size={22} />}
              accentClass="bg-cyan-700"
            />
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="Precio medio carta"
              value={formatCurrency(omnesMetrics.averagePrice)}
              hint={`${formatCurrency(omnesMetrics.minPrice)} mínimo · ${formatCurrency(omnesMetrics.maxPrice)} máximo`}
              icon={<HiOutlineCurrencyEuro size={22} />}
              accentClass="bg-fuchsia-600"
            />
            <SummaryCard
              label="Food cost medio"
              value={formatPercent(omnesMetrics.weightedFoodCostPercent, 2)}
              hint="Coste de producto sobre venta sin IVA"
              icon={<HiOutlineChartSquareBar size={22} />}
              accentClass="bg-rose-600"
            />
            <SummaryCard
              label="Peso coste laboral"
              value={formatPercent(omnesMetrics.labourCostShare, 2)}
              hint="Coste operativo aplicado frente a venta filtrada"
              icon={<HiOutlineTrendingDown size={22} />}
              accentClass="bg-sky-700"
            />
            <SummaryCard
              label="Dispersión de precios"
              value={formatNumber(omnesMetrics.priceSpreadRatio, 2)}
              hint={`Rango ${formatCurrency(omnesMetrics.priceRange)}`}
              icon={<HiOutlineTrendingUp size={22} />}
              accentClass="bg-violet-700"
            />
          </div>

          <div className="mt-4 grid gap-4 xl:grid-cols-[1.2fr,1fr]">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">Mix de carta</div>
              <div className="mt-1 text-sm text-gray-500">
                Reparto de la carta con coste informado dentro del periodo filtrado.
              </div>
              <div className="mt-4 space-y-3">
                <div>
                  <div className="mb-1 flex items-center justify-between text-sm text-gray-700">
                    <span>Estrellas</span>
                    <span className="font-semibold">{formatPercent(omnesMetrics.starsShare, 1)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-200">
                    <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${Math.min(100, omnesMetrics.starsShare)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between text-sm text-gray-700">
                    <span>Caballos de batalla</span>
                    <span className="font-semibold">{formatPercent(omnesMetrics.plowhorsesShare, 1)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-200">
                    <div className="h-2 rounded-full bg-amber-500" style={{ width: `${Math.min(100, omnesMetrics.plowhorsesShare)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between text-sm text-gray-700">
                    <span>Perros</span>
                    <span className="font-semibold">{formatPercent(omnesMetrics.dogsShare, 1)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-200">
                    <div className="h-2 rounded-full bg-rose-500" style={{ width: `${Math.min(100, omnesMetrics.dogsShare)}%` }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">Bandas de precio OMNES</div>
              <div className="mt-1 text-sm text-gray-500">
                Distribución de platos por bandas alrededor del precio medio actual.
              </div>
              <div className="mt-4 space-y-3">
                {omnesMetrics.distributionByPriceBand.map((band) => (
                  <div key={band.key}>
                    <div className="mb-1 flex items-center justify-between text-sm text-gray-700">
                      <span>{band.label}</span>
                      <span className="font-semibold">{band.count} · {formatPercent(band.share, 1)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-200">
                      <div className="h-2 rounded-full bg-orange-500" style={{ width: `${Math.min(100, band.share)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-sm font-semibold text-gray-900">Recomendaciones automáticas</div>
            <div className="mt-1 text-sm text-gray-500">
              Señales rápidas para owner o manager sobre precio, mix y margen.
            </div>
            <div className="mt-4 grid gap-3 xl:grid-cols-[1.2fr,1fr]">
              <div className="space-y-2">
                {omnesMetrics.recommendations.map((item, index) => (
                  <div key={`${index}-${item}`} className="rounded-lg bg-white px-3 py-3 text-sm text-gray-700">
                    {item}
                  </div>
                ))}
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="rounded-lg bg-white px-3 py-3">
                  Base analizada: {omnesMetrics.eligibleCount} items con coste y {formatCurrency(omnesMetrics.totalRevenue)} de venta sin IVA.
                </div>
                <div className="rounded-lg bg-white px-3 py-3">
                  Coste estructural teórico: {formatPercent(omnesMetrics.structureCostShare, 2)} sobre la venta filtrada.
                </div>
                <div className="rounded-lg bg-white px-3 py-3">
                  Objetivo práctico inicial en bistro: food cost bajo control, suficiente mix de estrellas y neto medio sano.
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-600">Coste Operativo</div>
            <h2 className="mt-1 text-xl font-bold text-gray-900">Configuracion del local</h2>
            <p className="mt-1 text-sm text-gray-500">
              Esta capa prepara el paso de margen bruto a margen neto real.
            </p>
          </div>
          <div className="rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-600">
            Campos completados: <span className="font-semibold text-gray-900">{operationalSummary.completedFields}/5</span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 xl:landscape:grid-cols-[1.8fr,1fr]">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 max-lg:portrait:p-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">IVA venta por defecto (%)</label>
                <input
                  name="default_sale_vat_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="21"
                  value={operationalSettings.default_sale_vat_rate}
                  onChange={handleOperationalSettingsChange}
                  disabled={!canEditOperationalSettings || !settingsLoaded}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100"
                />
                <p className="mt-1 text-xs text-gray-500">Se aplica a productos nuevos; cada producto puede cambiarlo (hostelería 10%).</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Coste mano de obra / hora</label>
                <input
                  name="labour_cost_per_hour"
                  type="number"
                  step="0.01"
                  value={operationalSettings.labour_cost_per_hour}
                  onChange={handleOperationalSettingsChange}
                  disabled={!canEditOperationalSettings || !settingsLoaded}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Modo de reparto</label>
                <select
                  name="allocation_mode"
                  value={operationalSettings.allocation_mode}
                  onChange={handleOperationalSettingsChange}
                  disabled={!canEditOperationalSettings || !settingsLoaded}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100"
                >
                  <option value="per_unit">Por unidad</option>
                  <option value="per_revenue">Por facturacion</option>
                  <option value="per_hour">Por hora</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Suministros mensuales</label>
                <input
                  name="utilities_monthly_cost"
                  type="number"
                  step="0.01"
                  value={operationalSettings.utilities_monthly_cost}
                  onChange={handleOperationalSettingsChange}
                  disabled={!canEditOperationalSettings || !settingsLoaded}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Alquiler mensual</label>
                <input
                  name="rent_monthly_cost"
                  type="number"
                  step="0.01"
                  value={operationalSettings.rent_monthly_cost}
                  onChange={handleOperationalSettingsChange}
                  disabled={!canEditOperationalSettings || !settingsLoaded}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Seguridad social mensual</label>
                <input
                  name="social_security_monthly_cost"
                  type="number"
                  step="0.01"
                  value={operationalSettings.social_security_monthly_cost}
                  onChange={handleOperationalSettingsChange}
                  disabled={!canEditOperationalSettings || !settingsLoaded}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Packaging mensual comun</label>
                <input
                  name="packaging_monthly_cost"
                  type="number"
                  step="0.01"
                  value={operationalSettings.packaging_monthly_cost}
                  onChange={handleOperationalSettingsChange}
                  disabled={!canEditOperationalSettings || !settingsLoaded}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:bg-gray-100"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 max-lg:portrait:gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="text-sm text-gray-500 max-lg:portrait:text-center">
                {canEditOperationalSettings
                  ? "Solo owner/superadmin puede cambiar esta configuracion."
                  : "Modo lectura: solo owner/superadmin puede cambiar esta configuracion."}
              </div>
              <div className="flex flex-col gap-2 max-lg:portrait:w-full md:max-lg:portrait:grid md:max-lg:portrait:grid-cols-2 lg:flex lg:shrink-0 lg:flex-row">
                <button
                  type="button"
                  onClick={loadOperationalSettings}
                  className="rounded bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 max-lg:portrait:min-h-[44px] max-lg:portrait:justify-center lg:py-2"
                >
                  Recargar
                </button>
                <button
                  type="button"
                  onClick={saveOperationalSettings}
                  disabled={!canEditOperationalSettings || isSavingSettings || !settingsLoaded}
                  className="rounded bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60 max-lg:portrait:min-h-[44px] max-lg:portrait:justify-center lg:py-2"
                >
                  {isSavingSettings ? "Guardando..." : "Guardar costes"}
                </button>
              </div>
            </div>

            {settingsMessage ? (
              <div className="mt-3 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm text-cyan-800">
                {settingsMessage}
              </div>
            ) : null}
          </div>

          <div className="space-y-3 max-lg:portrait:border-t max-lg:portrait:border-gray-200 max-lg:portrait:pt-4">
            <SummaryCard
              label="Coste fijo mensual"
              value={formatCurrency(operationalSummary.totalMonthlyFixed)}
              hint="Alquiler + suministros + SS + packaging comun"
              icon={<HiOutlineChartSquareBar size={22} />}
              accentClass="bg-cyan-600"
            />
            <SummaryCard
              label="Mano de obra / hora"
              value={formatCurrency(operationalSummary.labour)}
              hint="Base para prep time por producto"
              icon={<HiOutlineCurrencyEuro size={22} />}
              accentClass="bg-sky-600"
            />
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-800 shadow-sm max-lg:portrait:text-[13px]">
              El siguiente paso es usar esta configuracion para calcular margen neto real por producto y por periodo.
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-lime-600">Menu Engineering</div>
            <h2 className="mt-1 text-xl font-bold text-gray-900">Clasificacion por popularidad y contribucion</h2>
            <p className="mt-1 text-sm text-gray-500">
              Referencias del filtro actual: popularidad media {formatNumber(menuEngineering.averageUnits, 3)} uds ·
              contribucion media {formatCurrency(menuEngineering.averageContribution)}
            </p>
          </div>
          <div className="rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-600">
            Analizados: <span className="font-semibold text-gray-900">{menuEngineering.eligibleCount}</span> productos con coste
          </div>
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          <EngineeringQuadrant
            title="Estrellas"
            subtitle="Alta popularidad y alta contribucion. Mantener visibilidad y stock."
            rows={menuEngineering.stars}
            accentClass="bg-emerald-600"
            emptyText="No hay estrellas con el filtro actual."
            onEditProduct={openProductEditor}
          />
          <EngineeringQuadrant
            title="Puzzles"
            subtitle="Baja popularidad y alta contribucion. Revisar posicionamiento y empuje comercial."
            rows={menuEngineering.puzzles}
            accentClass="bg-cyan-600"
            emptyText="No hay puzzles con el filtro actual."
            onEditProduct={openProductEditor}
          />
          <EngineeringQuadrant
            title="Caballos de batalla"
            subtitle="Alta popularidad y baja contribucion. Revisar coste, gramaje o precio."
            rows={menuEngineering.plowhorses}
            accentClass="bg-amber-500"
            emptyText="No hay caballos de batalla con el filtro actual."
            onEditProduct={openProductEditor}
          />
          <EngineeringQuadrant
            title="Perros"
            subtitle="Baja popularidad y baja contribucion. Candidatos a retirar o rediseñar."
            rows={menuEngineering.dogs}
            accentClass="bg-rose-600"
            emptyText="No hay perros con el filtro actual."
            onEditProduct={openProductEditor}
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--card)]">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <TableHead>
              <Th className="px-4 py-3">Producto</Th>
              <Th className="px-4 py-3">Proveedor</Th>
              <Th className="px-4 py-3">Unidades</Th>
              <Th className="px-4 py-3">Despachado</Th>
              <Th className="px-4 py-3">Venta s/IVA</Th>
              <Th className="px-4 py-3">Coste s/IVA</Th>
              <Th className="px-4 py-3">Margen €/ud</Th>
              <Th className="px-4 py-3">Margen %</Th>
              <Th className="px-4 py-3">Contribucion</Th>
              <Th className="px-4 py-3">Coste op. /ud</Th>
              <Th className="px-4 py-3">Margen neto /ud</Th>
              <Th className="px-4 py-3">Margen neto %</Th>
              <Th className="px-4 py-3">Estado</Th>
            </TableHead>
            <tbody>
              {!loaded ? (
                <tr>
                  <Td align="center" colSpan={12} className="px-4 py-8 text-[var(--muted-foreground)]">Cargando rentabilidad...</Td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <Td align="center" colSpan={12} className="px-4 py-8 text-[var(--muted-foreground)]">No hay datos de rentabilidad para mostrar</Td>
                </tr>
              ) : (
                visibleRows.map((row) => (
                  // Sin cebra: la línea de separación ya hace ese trabajo, y con
                  // doce columnas las bandas alternas cansan más de lo que guían.
                  <Tr key={row.product_id}>
                    <Td className="px-4 py-3">
                      <div className="text-sm font-medium">{row.product_name}</div>
                      <div className="text-xs text-[var(--muted-foreground)]">{row.category_name || "Sin categoria"}</div>
                      <button
                        type="button"
                        onClick={() => openProductEditor(row.product_id)}
                        className="mt-2 inline-flex items-center gap-1 rounded-sm bg-[var(--primary-subtle-2)] px-2 py-1 text-xs font-semibold text-[var(--primary)] transition-colors hover:bg-[var(--primary-subtle)]"
                      >
                        <HiOutlinePencilAlt size={14} />
                        Abrir producto
                      </button>
                    </Td>
                    <Td className="px-4 py-3 text-sm">{row.supplier_name || "-"}</Td>
                    <Td className="px-4 py-3 text-sm">{formatNumber(row.units_sold, 3)}</Td>
                    <Td className="px-4 py-3 text-sm">
                      {row.dispensed != null && Number(row.dispensed) !== Number(row.units_sold)
                        ? formatNumber(row.dispensed, 3)
                        : <span className="text-gray-400">—</span>}
                    </Td>
                    <Td className="px-4 py-3 text-sm font-medium">{formatCurrency(row.revenue_ex_vat)}</Td>
                    <Td className="px-4 py-3 text-sm">{row.unit_cost_ex_vat == null ? "-" : formatCurrency(row.unit_cost_ex_vat)}</Td>
                    <Td className="px-4 py-3 text-sm">{row.gross_margin_amount == null ? "-" : formatCurrency(row.gross_margin_amount)}</Td>
                    <Td className="px-4 py-3 text-sm">{row.gross_margin_percent == null ? "-" : `${formatNumber(row.gross_margin_percent, 2)}%`}</Td>
                    <Td className="px-4 py-3 text-sm font-semibold">{row.contribution_total == null ? "-" : formatCurrency(row.contribution_total)}</Td>
                    <Td className="px-4 py-3 text-sm">{row.operational_cost_per_unit == null ? "-" : formatCurrency(row.operational_cost_per_unit)}</Td>
                    <Td className="px-4 py-3 text-sm font-semibold">{row.net_margin_amount == null ? "-" : formatCurrency(row.net_margin_amount)}</Td>
                    <Td className="px-4 py-3 text-sm">{row.net_margin_percent == null ? "-" : `${formatNumber(row.net_margin_percent, 2)}%`}</Td>
                    {/* Las cuatro insignias de estado quedan como estaban: son una escala
                        (sin coste / bajo / OK / top) y el sistema aún no tiene componente
                        de insignia —hay un `Club_Badge` en Figma sin contrapartida en
                        código—. Recolorearlas a ojo aquí sería inventarse la escala. */}
                    <Td className="px-4 py-3 text-sm">
                      {row.missing_cost ? (
                        <span className="inline-flex rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                          Sin coste
                        </span>
                      ) : row.gross_margin_percent != null && Number(row.gross_margin_percent) < 30 ? (
                        <span className="inline-flex rounded-full bg-rose-100 px-2 py-1 text-xs font-semibold text-rose-800">
                          Bajo margen
                        </span>
                      ) : row.gross_margin_percent != null && Number(row.gross_margin_percent) >= 60 ? (
                        <span className="inline-flex rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                          Top margen
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full bg-cyan-100 px-2 py-1 text-xs font-semibold text-cyan-800">
                          OK
                        </span>
                      )}
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ProductModal
        active={isProductModalOpen}
        handleModal={closeProductEditor}
        token={token}
        ownedShops={ownedShops}
        setErrorMessage={setError}
        productId={activeProductId}
        productData={null}
        onSaved={loadData}
        refreshTrigger={loaded ? rows.length : 0}
      />
    </div>
  );
}
